import { toSlug } from "common";

interface Stored {
  last?: { facility: string; payer: string };
  /** The payer last viewed in each facility, by facility */
  payers?: Record<string, string>;
}

const key = (username: string) => `lastViewed:${username}`;

const read = (username?: string): Stored => {
  if (!username) return {};
  try {
    return JSON.parse(localStorage.getItem(key(username)) ?? "null") ?? {};
  } catch {
    return {};
  }
};

/** Remembers which facility and payer a person last looked at, so tabs and links can bring them back to it */
export const rememberDashboard = (username: string | undefined, facility: string | undefined, payer: string | undefined) => {
  if (!username || !facility || !payer) return;
  const stored = read(username);
  const facilitySlug = toSlug(facility);
  const payerSlug = toSlug(payer);
  try {
    localStorage.setItem(
      key(username),
      JSON.stringify({ last: { facility: facilitySlug, payer: payerSlug }, payers: { ...stored.payers, [facilitySlug]: payerSlug } })
    );
  } catch {
    // Storage can be full or blocked; going back to where you were is a convenience only
  }
};

type Assignments = { facilities?: string[]; facilitiesPayers?: Record<string, string[]> };

const findAssigned = (user: Assignments | undefined, facility: string, payer?: string) => {
  const facilityName = user?.facilities?.find((name) => toSlug(name) === facility);
  if (!facilityName) return undefined;
  if (payer === undefined) return { facility: facilityName, payer: undefined };
  const payerName = user?.facilitiesPayers?.[facilityName]?.find((name) => toSlug(name) === payer);
  return payerName ? { facility: facilityName, payer: payerName } : undefined;
};

/** The payer last viewed in a facility, as the user's own name for it; nothing if it is not (or no longer) theirs */
export const lastPayerOf = (user: (Assignments & { username?: string }) | undefined, facilityName: string) => {
  const slug = read(user?.username).payers?.[toSlug(facilityName)];
  return slug ? findAssigned(user, toSlug(facilityName), slug)?.payer : undefined;
};

/** The dashboard last viewed, if the person can still see it */
export const lastDashboardOf = (user: (Assignments & { username?: string }) | undefined) => {
  const last = read(user?.username).last;
  return last ? findAssigned(user, last.facility, last.payer) : undefined;
};
